import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Textarea } from "./ui/textarea";
import { Alert, AlertDescription } from "./ui/alert";
import { FileText, Download, Plus, Trash2, Info } from "lucide-react";
import engagementService from "../services/engagementService";
import uploadService from "../services/uploadService";
import { useAuth } from "../hooks/useAuth";
import type { PricingInfo } from "../interface/PricingInfo";
import type { AdvisorProfile } from "../interface/AdvisorProfile";
import type { AnalystProfile } from "../interface/AnalystProfile";
import type { InvestorProfile } from "../interface/InvestorProfile";
import { BILLING_FREQUENCY } from "../utils/financialTaxonomy";

export interface EngagementAgreementDialogProps {
  professional: AdvisorProfile | AnalystProfile | InvestorProfile | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export interface EngagementAgreement {
  id: string;
  professionalId: string;
  clientId: string;
  scopeOfWork: string;
  deliverables: string;
  feeAmount: number;
  feeStructure: string;
  paymentTerms: string;
  termsAndConditions: string;
  effectiveStartDate?: string;
  effectiveEndDate?: string;
  docuSignUrl?: string;
  documentUrl?: string;
  status: "PENDING" | "SIGNED_BY_RECIPIENT" | "SIGNED_BY_BOTH" | "INVALIDATED";
  requesterId: string;
  recipientId: string;
  agreedFeeStructure: Array<{
    pricingInfo: PricingInfo;
    amount: number;
    frequency: string;
  }>;
}

export function EngagementAgreementDialog({ professional, isOpen, onOpenChange, onSuccess }: EngagementAgreementDialogProps) {
  const { userId, userRole } = useAuth();
  const [agreement, setAgreement] = useState<EngagementAgreement | null>(null);
  const [scopeOfWork, setScopeOfWork] = useState("");
  const [deliverables, setDeliverables] = useState("");
  const [fees, setFees] = useState<Array<{
    feeStructure: string;
    feeAmount: string;
    paymentTerms: string;
  }>>([{ feeStructure: "", feeAmount: "", paymentTerms: "MONTHLY" }]);
  const [termsAndConditions, setTermsAndConditions] = useState("");
  const [effectiveStartDate, setEffectiveStartDate] = useState("");
  const [effectiveEndDate, setEffectiveEndDate] = useState("");
  const [docuSignUrl, setDocuSignUrl] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingAgreement, setIsLoadingAgreement] = useState(false);
  const [pricingOptions, setPricingOptions] = useState<PricingInfo[]>([]);

  useEffect(() => {
    if (isOpen && professional && userId) {
      fetchExistingAgreement();
      if (userRole === "ADVISOR" || userRole === "ANALYST") {
        fetchPricingOptions();
      }
    }
  }, [isOpen, professional, userId, userRole]);

  const fetchPricingOptions = async () => {
    try {
      const response = await engagementService.getFeeStructurePricingList(userRole!, userId!);
      const data = response.data || response;
      setPricingOptions(data || []);
    } catch (error) {
      console.error("Error fetching pricing options:", error);
    }
  };

  const addFeeRow = () => {
    setFees([...fees, { feeStructure: "", feeAmount: "", paymentTerms: "MONTHLY" }]);
  };

  const removeFeeRow = (index: number) => {
    setFees(fees.filter((_, i) => i !== index));
  };

  const updateFeeRow = (index: number, field: string, value: string) => {
    const newFees = [...fees];
    (newFees[index] as any)[field] = value;
    setFees(newFees);
  };

  const fetchExistingAgreement = async () => {
    try {
      setIsLoadingAgreement(true);
      const response = await engagementService.getMyAgreements(userId);
      const agreements = response.data || response;

      const activeAgreement = agreements.find((a: EngagementAgreement) =>
        (a.requesterId === userId && a.recipientId === professional?.id) ||
        (a.requesterId === professional?.id && a.recipientId === userId)
      );

      if (activeAgreement && activeAgreement.status !== "INVALIDATED") {
        setAgreement(activeAgreement);
        setScopeOfWork(activeAgreement.scopeOfWork);
        setDeliverables(activeAgreement.deliverables);

        if (activeAgreement.agreedFeeStructure && activeAgreement.agreedFeeStructure.length > 0) {
          setFees(activeAgreement.agreedFeeStructure.map((p: any) => ({
            feeStructure: p.pricingInfo?.type || "",
            feeAmount: p.amount?.toString() || "",
            paymentTerms: p.frequency || ""
          })));
        } else {
          setFees([{
            feeStructure: activeAgreement.feeStructure,
            feeAmount: activeAgreement.feeAmount.toString(),
            paymentTerms: activeAgreement.paymentTerms
          }]);
        }

        setTermsAndConditions(activeAgreement.termsAndConditions);
        setEffectiveStartDate(activeAgreement.effectiveStartDate || "");
        setEffectiveEndDate(activeAgreement.effectiveEndDate || "");
        setDocuSignUrl(activeAgreement.docuSignUrl || "");
      } else {
        console.log("No active agreement found");
        setAgreement(null);
      }
    } catch (error) {
      console.error("Error fetching agreement:", error);
    } finally {
      setIsLoadingAgreement(false);
    }
  };

  if (!professional) return null;

  const isRequester = agreement ? agreement.requesterId === userId : true;
  const isRecipient = agreement ? agreement.recipientId === userId : false;

  const canModify = !agreement && (userRole === "ADVISOR" || userRole === "ANALYST");
  const canRecipientSign = agreement?.status === "PENDING" && isRecipient;
  const canRequesterSign = agreement?.status === "SIGNED_BY_RECIPIENT" && isRequester;
  const isLocked = agreement?.status === "SIGNED_BY_BOTH";
  const canInvalidate = isLocked && isRequester;

  const handleInitiate = async () => {
    const isFeesValid = fees.every(f => f.feeAmount && f.feeStructure && f.paymentTerms);
    if (!scopeOfWork || !deliverables || !isFeesValid || !termsAndConditions || !effectiveStartDate) {
      toast.error("Please fill in all required fields");
      return;
    }

    let uploadedFileKey = null;
    try {
      setIsSubmitting(true);

      let documentUrl = null;
      if (selectedFile) {
        const { url, key } = await uploadService.uploadFile(selectedFile, "agreements");
        documentUrl = url;
        uploadedFileKey = key;
      }

      const agreedFeeStructure = fees.map(f => {
        const opt = pricingOptions.find(p => p.type === f.feeStructure);
        return {
          pricingInfo: opt || { type: f.feeStructure, range: "", details: "" },
          amount: parseFloat(f.feeAmount),
          frequency: f.paymentTerms
        };
      });

      const agreementData = {
        requesterId: userId,
        recipientId: professional.id,
        professionalId: userRole === "INVESTOR" ? professional.id : userId,
        clientId: userRole === "INVESTOR" ? userId : professional.id,
        scopeOfWork,
        deliverables,
        feeAmount: parseFloat(fees[0]?.feeAmount || "0"),
        feeStructure: fees[0]?.feeStructure || "flat_fee",
        paymentTerms: fees[0]?.paymentTerms || "MONTHLY",
        termsAndConditions,
        effectiveStartDate,
        effectiveEndDate: effectiveEndDate || undefined,
        docuSignUrl,
        documentUrl: documentUrl,
        status: "PENDING",
        agreedFeeStructure: agreedFeeStructure
      };

      await engagementService.createAgreement(agreementData);
      toast.success("Engagement initiated successfully");
      setSelectedFile(null);
      fetchExistingAgreement();
    } catch (error: any) {
      if (uploadedFileKey) await uploadService.deleteFile(uploadedFileKey);
      toast.error(error.message || "Failed to initiate engagement");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRecipientSign = async () => {
    let uploadedFileKey = null;
    try {
      setIsSubmitting(true);
      let documentUrl = agreement?.documentUrl;

      if (selectedFile) {
        const { url, key } = await uploadService.uploadFile(selectedFile, "agreements");
        documentUrl = url;
        uploadedFileKey = key;
      }

      await engagementService.updateAgreementStatus(agreement!.id, {
        status: "SIGNED_BY_RECIPIENT",
        documentUrl: documentUrl,
        docuSignUrl: docuSignUrl
      });

      toast.success("Agreement signed and document updated!");
      setSelectedFile(null);
      fetchExistingAgreement();
    } catch (error: any) {
      if (uploadedFileKey) await uploadService.deleteFile(uploadedFileKey);
      toast.error(error.message || "Failed to sign agreement");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRequesterSign = async () => {
    let uploadedFileKey = null;
    try {
      setIsSubmitting(true);
      let documentUrl = agreement?.documentUrl;

      if (selectedFile) {
        const { url, key } = await uploadService.uploadFile(selectedFile, "agreements");
        documentUrl = url;
        uploadedFileKey = key;
      }

      await engagementService.updateAgreementStatus(agreement!.id, {
        status: "SIGNED_BY_BOTH",
        documentUrl: documentUrl,
        docuSignUrl: docuSignUrl
      });
      toast.success("Engagement fully executed and locked!");
      setSelectedFile(null);
      fetchExistingAgreement();
    } catch (error: any) {
      if (uploadedFileKey) await uploadService.deleteFile(uploadedFileKey);
      toast.error(error.message || "Failed to complete signature");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInvalidate = async () => {
    try {
      setIsSubmitting(true);
      await engagementService.invalidateAgreement(agreement!.id);
      toast.success("Agreement invalidated. You can now initiate a new one.");
      setAgreement(null);
    } catch (error: any) {
      toast.error(error.message || "Failed to invalidate agreement");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownload = async () => {
    if (!agreement?.documentUrl) return;
    try {
      await uploadService.downloadFile(agreement.documentUrl);
    } catch (error: any) {
      toast.error(error.message || "Failed to download agreement");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {agreement ? `Agreement Status: ${agreement.status.replace(/_/g, " ")}` : "Create Engagement Agreement"}
          </DialogTitle>
          <DialogDescription>
            {agreement
              ? `Current engagement terms with ${professional.name}`
              : `Define the professional relationship terms with ${professional.name}`
            }
          </DialogDescription>
        </DialogHeader>

        {!agreement && userRole === "INVESTOR" && (
          <Alert className="bg-blue-50 border-blue-200 mt-4">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-700">
              Only Advisors or Analysts can initiate an engagement agreement. Once initiated, you will be able to review and sign it here.
            </AlertDescription>
          </Alert>
        )}

        {isLoadingAgreement ? (
          <div className="py-20 text-center">Loading agreement details...</div>
        ) : (
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="scope">Scope of Work *</Label>
              <Textarea
                id="scope"
                disabled={!!agreement || userRole === "INVESTOR"}
                value={scopeOfWork}
                onChange={(e) => setScopeOfWork(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="deliverables">Deliverables *</Label>
              <Textarea
                id="deliverables"
                disabled={!!agreement || userRole === "INVESTOR"}
                value={deliverables}
                onChange={(e) => setDeliverables(e.target.value)}
              />
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Fee Structures *</Label>
                {canModify && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addFeeRow}
                    disabled={fees.length >= (pricingOptions.length > 0 ? pricingOptions.length : 4)}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Fee Structure
                  </Button>
                )}
              </div>

              {fees.map((fee, index) => (
                <div key={index} className="space-y-4 p-4 border rounded-lg relative bg-muted/30">
                  {canModify && fees.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute top-2 right-2 h-8 w-8 text-destructive"
                      onClick={() => removeFeeRow(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor={`feeStructure-${index}`}>Fee Structure</Label>
                      <Select
                        disabled={!!agreement || userRole === "INVESTOR"}
                        value={fee.feeStructure}
                        onValueChange={(val) => updateFeeRow(index, "feeStructure", val)}
                      >
                        <SelectTrigger id={`feeStructure-${index}`}>
                          <SelectValue placeholder="Select fee structure" />
                        </SelectTrigger>
                        <SelectContent>
                          {pricingOptions.length > 0 ? (
                            pricingOptions.map((opt, optIndex) => {
                              const isAlreadySelected = fees.some((f, i) => i !== index && f.feeStructure === opt.type);
                              return (
                                <SelectItem
                                  key={`${opt.type}-${optIndex}`}
                                  value={opt.type}
                                  disabled={isAlreadySelected}
                                >
                                  <div className="flex flex-col text-xs">
                                    <span className="font-semibold uppercase">{opt.type.replace(/_/g, " ")}</span>
                                    <span className="text-muted-foreground">{opt.range} - {opt.details}</span>
                                  </div>
                                </SelectItem>
                              );
                            })
                          ) : (
                            <>
                              <SelectItem value="flat_fee" disabled={fees.some((f, i) => i !== index && f.feeStructure === "flat_fee")}>Flat Fee</SelectItem>
                              <SelectItem value="hourly" disabled={fees.some((f, i) => i !== index && f.feeStructure === "hourly")}>Hourly Rate</SelectItem>
                              <SelectItem value="project_based" disabled={fees.some((f, i) => i !== index && f.feeStructure === "project_based")}>Project Based</SelectItem>
                              <SelectItem value="aum_based" disabled={fees.some((f, i) => i !== index && f.feeStructure === "aum_based")}>AUM Based</SelectItem>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`feeAmount-${index}`}>Fee Amount ($) *</Label>
                      <Input
                        id={`feeAmount-${index}`}
                        type="number"
                        disabled={!!agreement || userRole === "INVESTOR"}
                        value={fee.feeAmount}
                        onChange={(e) => updateFeeRow(index, "feeAmount", e.target.value)}
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`paymentTerms-${index}`}>Payment Terms</Label>
                    <Select
                      disabled={!!agreement || userRole === "INVESTOR"}
                      value={fee.paymentTerms}
                      onValueChange={(val) => updateFeeRow(index, "paymentTerms", val)}
                    >
                      <SelectTrigger id={`paymentTerms-${index}`}>
                        <SelectValue placeholder="Select payment terms" />
                      </SelectTrigger>
                      <SelectContent>
                        {BILLING_FREQUENCY.map((freq: { value: string; label: string }) => (
                          <SelectItem key={freq.value} value={freq.value}>
                            {freq.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
            <div className="space-y-2">
              <Label htmlFor="terms">Terms and Conditions *</Label>
              <Textarea
                id="terms"
                disabled={!!agreement || userRole === "INVESTOR"}
                value={termsAndConditions}
                onChange={(e) => setTermsAndConditions(e.target.value)}
                className="min-h-[100px]"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="effectiveStartDate">Effective Start Date *</Label>
                <Input
                  id="effectiveStartDate"
                  type="date"
                  disabled={!!agreement || userRole === "INVESTOR"}
                  value={effectiveStartDate}
                  onChange={(e) => setEffectiveStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="effectiveEndDate">Effective End Date</Label>
                <Input
                  id="effectiveEndDate"
                  type="date"
                  disabled={!!agreement || userRole === "INVESTOR"}
                  value={effectiveEndDate}
                  onChange={(e) => setEffectiveEndDate(e.target.value)}
                />
              </div>
            </div>

            {(agreement?.documentUrl) && (
              <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-blue-600" />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">
                      {agreement.status === "PENDING" ? "Draft Agreement" : "Signed Document"}
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      {agreement.status === "PENDING" ? "Review the terms provided by the requester" : "Fully executed agreement"}
                    </span>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={handleDownload}>
                  <Download className="h-3 w-3 mr-2" />
                  Download
                </Button>
              </div>
            )}

            {canRequesterSign && (
              <div className="space-y-4 border-t pt-4">
                <div className="space-y-2">
                  <Label>Upload Fully Executed Agreement (Optional)</Label>
                  <Input
                    type="file"
                    onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                    className="cursor-pointer"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="docusign-requester">Final DocuSign URL (Optional)</Label>
                  <Input
                    id="docusign-requester"
                    placeholder="https://docusign.com/..."
                    value={docuSignUrl}
                    onChange={(e) => setDocuSignUrl(e.target.value)}
                  />
                </div>
              </div>
            )}

            {canRecipientSign && (
              <div className="space-y-4 border-t pt-4">
                <div className="space-y-2">
                  <Label className="text-blue-900">Step 1: Review Draft</Label>
                  <p className="text-xs text-muted-foreground">Download and review the draft agreement above before signing.</p>
                </div>
                <div className="space-y-2">
                  <Label>Step 2: Upload Signed Version *</Label>
                  <Input
                    type="file"
                    onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                    className="cursor-pointer"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">OR</span>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="docusign-recipient">Updated DocuSign URL (Optional)</Label>
                  <Input
                    id="docusign-recipient"
                    placeholder="https://docusign.com/..."
                    value={docuSignUrl}
                    onChange={(e) => setDocuSignUrl(e.target.value)}
                  />
                </div>
              </div>
            )}

            {!agreement && (
              <div className="space-y-4 border-t pt-4">
                <div className="space-y-2">
                  <Label>Agreement Document (Optional)</Label>
                  <Input
                    type="file"
                    onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                    className="cursor-pointer"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">OR</span>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="docusign">DocuSign URL (Optional)</Label>
                  <Input
                    id="docusign"
                    placeholder="https://docusign.com/..."
                    value={docuSignUrl}
                    onChange={(e) => setDocuSignUrl(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {canModify && (
            <Button onClick={handleInitiate} disabled={isSubmitting}>
              Initiate Engagement
            </Button>
          )}
          {canRecipientSign && (
            <Button onClick={handleRecipientSign} disabled={isSubmitting}>
              Upload & Sign
            </Button>
          )}
          {canRequesterSign && (
            <Button onClick={handleRequesterSign} disabled={isSubmitting}>
              Final Signature
            </Button>
          )}
          {canInvalidate && (
            <Button variant="destructive" onClick={handleInvalidate} disabled={isSubmitting}>
              Invalidate & Start New
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
